import React, { useCallback, useContext, useEffect, useState } from 'react';
import SearchIcon from '@mui/icons-material/Search';
import { createPortal } from 'react-dom';
import {
  Button,
  MenuItem,
  TextField,
  ListItemText,
  OutlinedInput,
  Checkbox,
  Select,
  FormControl,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import ItemSummaryReportService from 'services/ItemSummaryReportService';
import SalespersonsService from 'services/SalesPersonService';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SyncIcon from '@mui/icons-material/Sync';
import { saveAs } from 'file-saver';
import 'date-fns';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import Info from 'components/utils/Alert/Info';
import setDelay from 'helpers/LoadingDelay';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import AlertHelper from 'helpers/AlertHelper';
import Error from 'components/utils/Alert/Error';
import { ShopContext } from 'Context/ShopContext';
import { AlertMessagesContext } from 'react-alert-messages';
import CsvHelper from 'helpers/CsvHelper';
import { filterEndDate } from 'components/utils/FilterEndDate';
import PrintIcon from '@mui/icons-material/Print';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import TerminalService from 'services/TerminalServices';
import { SelectFilterStyle, PRINT_TYPES } from 'const';
import { useReportPrint } from 'hooks/useReportPrint';
import PrintDocument from 'components/utils/PrintDocument/index';
import DateLimitDialog from 'components/popups/DateLimitDialog';
import DateTimeRange from 'components/utils/DateTimeRange/DateTimeRange';
import { dynamicDateRange } from 'services/DynamicDateRangeService';
import { InventoryContext } from 'Context/InventoryContext';
import { useTranslation } from 'react-i18next';

const ITEM_SORTING_OPTIONS = [
  { value: 'QUANTITY_DESC', labelKey: 'most_return' },
  { value: 'QUANTITY_ASC', labelKey: 'least_return' },
  { value: 'ITEM', labelKey: 'item_name_sort' },
  { value: 'CATEGORY', labelKey: 'category_name_sort' },
];

function ReturnedItemSummary() {
  const { t: tCommon } = useTranslation('common');
  const { t: tReport } = useTranslation('report');
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const { categories, getCategories } = useContext(InventoryContext);

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [filterFromDate, setFilterFromDate] = useState(getDefaultStartTime());
  const [filterToDate, setFilterToDate] = useState(filterEndDate());
  const [filterCategory, setFilterCategory] = useState([]);
  const [filterTax, setFilterTax] = useState('all');
  const [items, setItems] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [taxes, setTaxes] = useState('');
  const [errorMsg, setErrorMsg] = useState();
  const [terminals, setTerminals] = useState([]);
  const [filterTerminal, setFilterTerminal] = useState([]);
  const [salesPersons, setSalesPersons] = useState([]);
  const [filterSalesperson, setFilterSalesperson] = useState([]);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);
  const [sorting, setSorting] = useState('QUANTITY_DESC');
  const [prevFilterData, setPrevFilterData] = useState({
    filterFromDate,
    filterToDate,
    filterCategory,
    filterTax,
    sorting,
  });
  const [dateLimitPopup, setDateLimitPopup] = useState(false);
  const { reportToPrint, handlePrint: handlePrintReportHook } = useReportPrint(shop, tCommon, tReport, DateTimeHelpers);

  const CSV_COLUMNS = {
    plu: tCommon('PLU'),
    name: tCommon('Item Name'),
    hsn: tCommon('HSN'),
    price: {
      title: tCommon('Unit Price'),
      getValue: (datum) => ShopsHelper.getAmountFormatted(shop, datum?.price || 0),
    },
    tax: tCommon('Tax(%)'),
    tax_amount: {
      title: tCommon('Tax Amount'),
      getValue: (datum) => ShopsHelper.getAmountFormatted(shop, datum?.tax_amount || 0),
    },
    cess: tCommon('Additional Charges(%)'),
    cess_amount: {
      title: tCommon('Additional Charges'),
      getValue: (datum) => ShopsHelper.getAmountFormatted(shop, datum?.cess_amount || 0),
    },
    sold_quantity: tCommon('Sold Quantity'),
    unit: tCommon('Unit'),
    total: {
      title: tCommon('Total Amount'),
      getValue: (datum) => ShopsHelper.getAmountFormatted(shop, datum?.total || 0),
    },
  };

  function getDefaultStartTime() {
    const date = new Date();
    date.setHours(0, 0, 0, 0);
    return date;
  }

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return (
      filterFromDate !== prevFilterData.filterFromDate ||
      filterToDate !== prevFilterData.filterToDate ||
      filterCategory !== prevFilterData.filterCategory ||
      filterTax !== prevFilterData.filterTax ||
      filterTerminal !== prevFilterData.filterTerminal ||
      filterSalesperson !== prevFilterData.filterSalesperson ||
      sorting !== prevFilterData.sorting
    );
  }, [
    filterFromDate,
    filterToDate,
    filterCategory,
    filterTax,
    filterTerminal,
    filterSalesperson,
    sorting,
    prevFilterData.filterFromDate,
    prevFilterData.filterToDate,
    prevFilterData.filterCategory,
    prevFilterData.filterTax,
    prevFilterData.filterTerminal,
    prevFilterData.filterSalesperson,
    prevFilterData.sorting,
  ]);

  useEffect(() => {
    generateItemSummaryReports();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    getSalespersons();
    getTerminals();
    getVat();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  const handlePrintReport = () => {
    handlePrintReportHook(
      tReport('Returned Item Summary Report'),
      filterFromDate,
      filterToDate,
      headerData,
      filteredItems,
      {
        [tCommon('Total Tax')]: `${shop?.currency || ''} ${summary.totalTax}`,
        [tCommon('Total Additional Tax')]: `${shop?.currency || ''} ${summary.totalAdditionalTax}`,
        [tCommon('Total Amount')]: `${shop?.currency || ''} ${summary.totalAmount}`,
      }
    );
  };

  const generateItemSummaryReports = () => {
    const { startDate, endDate } = dynamicDateRange('RETURNED_ITEM_SUMMARY');

    setFilterFromDate(startDate);
    setFilterToDate(endDate);

    loadItemSummaryReports({
      startTime: startDate,
      endTime: endDate,
      category: filterCategory,
      tax: filterTax,
      cashier_ids: filterSalesperson,
      terminal_ids: filterTerminal,
      sorting,
    });

    setIsShowGenerateButton(false);
    setPrevFilterData({
      filterFromDate: startDate,
      filterToDate: endDate,
      filterCategory,
      filterTax,
      filterSalesperson,
      filterTerminal,
      sorting
    });
  };

  const loadItemSummaryReports = async ({
    startTime = filterFromDate,
    endTime = filterToDate,
    category = filterCategory,
    tax = filterTax,
    cashier_ids = filterSalesperson,
    terminal_ids = filterTerminal,
    item_sorting = sorting,
  } = {}) => {
    if (DateTimeHelpers.reportDateLimit(startTime, endTime)) {
      setDateLimitPopup(true);
      return;
    }
    setLoadingIndicator(true);
    try {
      const timezoneFreeStartTime = DateTimeHelpers.getTimezoneOffsetDate(startTime);
      const timezoneFreeEndTime = DateTimeHelpers.getTimezoneOffsetDate(endTime);

      const data = {
        filter_start_time: timezoneFreeStartTime,
        filter_end_time: timezoneFreeEndTime,
        category_id: category === 'all' ? undefined : category,
        filter_tax: tax === 'all' ? '' : [tax],
        terminal_ids: terminal_ids.length ? terminal_ids : null,
        cashier_ids: cashier_ids.length ? cashier_ids : null,
        sorting: item_sorting,
      };

      const resp = await ItemSummaryReportService.getReturnedItemSummaryReport(data);
      setItems(resp);
      setDelay(setLoadingIndicator);
    } catch (err) {
      AlertHelper.setShortMessage(setErrorMsg, err.message);
    }
    setLoadingIndicator(false);
  };

  const getVat = async () => {
    const res = await ItemSummaryReportService.getVat();
    setTaxes(res);
  };

  const getSalespersons = async () => {
    const res = await SalespersonsService.getSalesPersons();
    setSalesPersons(res);
  };

  const getTerminals = async () => {
    const res = await TerminalService.getTerminals();
    setTerminals(res);
  };

  const toCsvBtnPressed = async () => {
    const titleLine = tReport('Returned Item Summary Report of for {{from}} to {{to}}', {
      from: DateTimeHelpers.convertDateToDMY(filterFromDate),
      to: DateTimeHelpers.convertDateToDMY(filterToDate),
    });

    const csvWithoutTotal = CsvHelper.getString(filteredItems, CSV_COLUMNS, titleLine);

    // Create total row
    const summary = _getItemsReportSummary(filteredItems);
    const fields = Object.keys(CSV_COLUMNS);
    const totalRowValues = fields.map((key) => {
      switch (key) {
        case 'name':
          return tCommon('Total');
        case 'total':
          return summary.totalAmount;
        case 'tax_amount':
          return summary.totalTax;
        case 'cess_amount':
          return summary.totalAdditionalTax;
        default:
          return '';
      }
    });

    const totalRow = totalRowValues.join(',') + '\n';
    const csv = csvWithoutTotal + '\n' + totalRow;

    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `RETURNED-ITEMS-${dateTime}.csv`);

    postAlertMessage({
      text: tReport('Exported to excel successfully'),
      type: 'success',
    });
  };

  const _getItemsFiltered = (items, searchText) => {
    if (!searchText) {
      return items;
    }

    return items.filter(
      (item) =>
        item.plu.toString().includes(searchText.toLowerCase()) ||
        item.name.toLowerCase().includes(searchText.toLowerCase())
    );
  };

  const _getItemsReportSummary = (items) => {
    let totalAmount = 0;
    let totalTax = 0;
    let totalAdditionalTax = 0;
    const salesItems = items.filter(
      (item) => item.name?.toLowerCase() !== 'discount' && String(item.plu) !== '9999996'
    );

    salesItems.forEach((item) => {
      totalAmount += item.total;
      totalTax += item.tax_amount;
      totalAdditionalTax += item.cess_amount;
    });

    return {
      totalAmount: ShopsHelper.getAmountFormatted(shop, totalAmount),
      totalTax: ShopsHelper.getAmountFormatted(shop, totalTax),
      totalAdditionalTax: ShopsHelper.getAmountFormatted(shop, totalAdditionalTax),
    };
  };

  const headerData = [
    {
      label: tCommon('PLU'),
      id: 'plu',
      type: 'text',
    },
    {
      label: tCommon('Item Name'),
      id: 'name',
      type: 'text',
    },
    {
      label: tCommon('Unit Price'),
      id: 'price',
      type: 'floatAmount',
    },
    {
      label: tCommon('Tax(%)'),
      id: 'tax',
      type: 'text',
    },
    {
      label: tCommon('Tax Amount'),
      id: 'tax_amount',
      type: 'floatAmount',
    },
    {
      label: tCommon('Additional Tax(%)'),
      id: 'cess',
      type: 'text',
    },
    {
      label: tCommon('Additional Tax'),
      id: 'cess_amount',
      type: 'floatAmount',
    },
    {
      label: tCommon('Sold Quantity'),
      id: 'sold_quantity',
      type: 'callback',
      viewRender: (item) => {
        if (item.sold_quantity === '' || isNaN(item.sold_quantity)) {
          return item.sold_quantity;
        }
        return `${ShopsHelper.getAmountFormatted(shop, item.sold_quantity)} ${item.unit || ''}`;
      },
    },
    {
      label: tCommon('Total Amount'),
      id: 'total',
      type: 'callback',
      viewRender: (item) => {
        return `${shop?.currency || ''} ${item.total}`;
      },
    },
  ];

  const filteredItems = _getItemsFiltered(items, searchText);
  const summary = _getItemsReportSummary(filteredItems);

  useEffect(() => {
    getCategories();
  }, [getCategories]);

  return (
    <div className={styles.contentWrapper}>
      <div className={styles.titleSec}>
        <span className={styles.title}>
          {' '}
          {tCommon('Reports')}
          <span className={styles.menuTitle}>{tCommon('Generation')}</span>
        </span>
        <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Print Report')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={handlePrintReport}
          >
            <PrintIcon className={styles.actionBtnIcon} />
            {tCommon('Print')}
          </Button>
        </div>
        <div style={{ justifyContent: 'flex-end' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon('Export As')}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={toCsvBtnPressed}
          >
            <ImportExportIcon className={styles.actionBtnIcon} />
            {tCommon('CSV')}
          </Button>
        </div>
      </div>
      <div className={styles.changeable}>
        <div className={styles.filterSec}>
          <div className={styles.headTitle}>
            <h2 className={styles.subTitle}>
              {tReport('Returned Item Summary Report of for {{from}} to {{to}}', {
                from: DateTimeHelpers.convertDateToDMY(filterFromDate),
                to: DateTimeHelpers.convertDateToDMY(filterToDate),
              })}
            </h2>
          </div>
          <div className={styles.filerInputSec}>
            <div className={styles.searchSec}>
              <input
                type="text"
                value={searchText}
                onChange={(e) => {
                  setSearchText(e.target.value);
                }}
                className={styles.searchInput}
                placeholder={tCommon('Search by plu, name')}
              />
              <Tooltip title="Search" placement="bottom">
                <SearchIcon className={styles.searchIcon} />
              </Tooltip>
            </div>
          </div>
        </div>
        <div className={styles.actionButtons}>
          <div className={styles.filterDiv}>
            <DateTimeRange
              setFilterFromDate={setFilterFromDate}
              setFilterToDate={setFilterToDate}
              storageKey="RETURNED_ITEM_SUMMARY"
            />
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Category')}</label>
            </div>
            <div>
              <FormControl>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  displayEmpty
                  value={filterCategory}
                  onChange={(e) => setFilterCategory(e.target.value)}
                  input={<OutlinedInput />}
                  style={{ width: 135, height: 40 }}
                  renderValue={(filterCategory) => {
                    if (filterCategory.length === 0) return <span>{tCommon('All')}</span>;
                    const selectedNames = filterCategory.map(
                      (id) => categories.find((category) => category.id === id).name
                    );
                    return selectedNames.join(', ');
                  }}
                  MenuProps={SelectFilterStyle}
                >
                  {(categories || []).map((category) => (
                    <MenuItem key={category.id} value={category.id}>
                      <Checkbox checked={filterCategory.includes(category.id)} color="primary" />
                      <ListItemText primary={category?.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Terminal')}</label>
            </div>
            <div>
              <FormControl>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  displayEmpty
                  value={filterTerminal}
                  onChange={(e) => setFilterTerminal(e.target.value)}
                  input={<OutlinedInput />}
                  style={{ width: 135, height: 40 }}
                  renderValue={(filterTerminal) => {
                    if (filterTerminal.length === 0) return <span>{tCommon('All')}</span>;
                    const selectedNames = filterTerminal.map((id) => terminals.find((tr) => tr.id === id).name);
                    return selectedNames.join(', ');
                  }}
                  MenuProps={SelectFilterStyle}
                >
                  {terminals.map((terminal) => (
                    <MenuItem key={terminal.id} value={terminal.id}>
                      <Checkbox checked={filterTerminal.includes(terminal.id)} color="primary" />
                      <ListItemText primary={terminal.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Salesperson')}</label>
            </div>
            <div>
              <FormControl>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  displayEmpty
                  value={filterSalesperson}
                  onChange={(e) => setFilterSalesperson(e.target.value)}
                  input={<OutlinedInput />}
                  style={{ width: 135, height: 40 }}
                  renderValue={(filterSalesperson) => {
                    if (filterSalesperson.length === 0) return <span>{tCommon('All')}</span>;
                    const selectedNames = filterSalesperson.map(
                      (id) => salesPersons.find((person) => person.id === id).name
                    );
                    return selectedNames.join(', ');
                  }}
                  MenuProps={SelectFilterStyle}
                >
                  {salesPersons.map((salesperson) => (
                    <MenuItem key={salesperson.id} value={salesperson.id}>
                      <Checkbox checked={filterSalesperson.includes(salesperson.id)} color="primary" />
                      <ListItemText primary={salesperson?.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Tax')}</label>
            </div>
            <div>
              <TextField
                select
                name="category"
                size="small"
                variant="outlined"
                defaultValue={filterTax}
                color="primary"
                className={styles.textfeild}
                onChange={(event) => {
                  setFilterTax(event.target.value);
                }}
              >
                {taxes &&
                  taxes.map((vat) => (
                    <MenuItem key={vat} value={vat}>
                      {vat}
                    </MenuItem>
                  ))}
                <MenuItem value="all">{tCommon('All')}</MenuItem>
              </TextField>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon("Sort by")}</label>
            </div>
            <div>
              <TextField
                select
                size="small"
                variant="outlined"
                defaultValue={sorting}
                color="primary"
                className={styles.textfeild}
                onChange={(event) => {
                  setSorting(event.target.value);
                }}
              >
                {Object.values(ITEM_SORTING_OPTIONS)?.map((op) => (
                  <MenuItem key={op.value} value={op.value}>
                    {tCommon(op.labelKey)}
                  </MenuItem>
                ))}
              </TextField>
            </div>
          </div>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon('Generate Report')}</label>
            </div>
            <div>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a' }}
                onClick={generateItemSummaryReports}
                disabled={isShowGenerateButton === false || loadingIndicator}
              >
                {loadingIndicator ? (
                  <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                ) : (
                  <SyncIcon className={styles.actionBtnIcon} />
                )}
                {tCommon('Generate')}
              </Button>
            </div>
          </div>
        </div>
      </div>
      {errorMsg && (
        <div className={styles.marginTop}>
          <Error title={errorMsg} />
        </div>
      )}

      {isShowGenerateButton === null ? (
        <Info title={tReport('Set filters and click generate button')} content={tReport('Reports are generated based on the filters. Please set filters and click generate button to generate the report.')} />
      ) : (
        items &&
        (filteredItems && filteredItems.length ? (
          <div className={styles.tableContainer}>
            <Grayout open={isShowGenerateButton} />
            <DataTable
              columns={headerData}
              rows={filteredItems}
              rowKey="id"
              summary={{
                name: tCommon('Total'),
                total: `${shop?.currency} ${summary.totalAmount}`,
                tax_amount: `${shop?.currency || ''} ${summary.totalTax}`,
                cess_amount: `${shop?.currency || ''} ${summary.totalAdditionalTax}`,
              }}
            />
          </div>
        ) : (
          !loadingIndicator && (
            <Info severity="warning" title={tReport('Oh no, there are no sales matching to your filter')} content={tReport('There are no sales matching to your filter. Please verify your inputs to generate report.')} />
          )
        ))
      )}
      {dateLimitPopup && (
        <DateLimitDialog
          handleClose={() => {
            setDateLimitPopup(false);
          }}
        />
      )}
      {reportToPrint &&
        createPortal(<PrintDocument type={PRINT_TYPES.REPORT} data={reportToPrint} shop={shop} />, document.body)}
    </div>
  );
}

export default withConsoleBase(ReturnedItemSummary);
